import Settings from "../dependencies/custom/settings";
import Utilities from "../dependencies/custom/react-utilities";

function replacePathParams(url, pathParams = {}) {
  return url.replace(/:([A-Za-z0-9_]+)/g, (_, key) => {
    const value = pathParams[key];
    return value === undefined || value === null
      ? `:${key}`
      : encodeURIComponent(value);
  });
}

function appendQuery(url, query = {}) {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, item));
      return;
    }
    params.set(key, value);
  });
  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

function getEndpoint(key, pathParams, query) {
  const configuredUrl = Settings.urls.lms[key];
  if (!configuredUrl) throw new Error(`Unknown LMS endpoint: ${key}`);
  return appendQuery(replacePathParams(configuredUrl, pathParams), query);
}

export function getLmsFileStreamUrl(fileId, studentID) {
  return (
    getEndpoint("learnerContentStream", { fileId }) +
    (studentID ? `?studentID=${encodeURIComponent(studentID)}` : "")
  );
}

export function getLmsListItems(payload) {
  if (Array.isArray(payload)) {
    return {
      items: payload,
      count: payload.length,
      next: null,
      previous: null,
    };
  }
  return {
    items: Array.isArray(payload?.results) ? payload.results : [],
    count: Number(payload?.count || 0),
    next: payload?.next || null,
    previous: payload?.previous || null,
  };
}

export function getLmsErrorMessage(response, fallback = "LMS request failed") {
  const payload = response?.data;
  if (typeof payload === "string" && payload) return payload;
  if (payload?.error) return payload.error;
  if (payload?.detail) return payload.detail;
  if (payload && typeof payload === "object") {
    const firstError = Object.values(payload).find((value) => value);
    if (Array.isArray(firstError)) return firstError[0];
    if (typeof firstError === "string") return firstError;
  }
  return response?.msg || fallback;
}

export function createLmsApi(navigate) {
  const request = (method, endpointKey, options = {}) => {
    const url = getEndpoint(endpointKey, options.pathParams, options.query);
    return Utilities.fetchWithAuth(
      method,
      url,
      options.body,
      navigate,
      undefined,
      options.requestOptions,
    );
  };

  const resource = (endpointKey, detailKey) => ({
    list: (query) => request("GET", endpointKey, { query }),
    get: (id) => request("GET", detailKey, { pathParams: { id } }),
    create: (body) => request("POST", endpointKey, { body }),
    update: (id, body) =>
      request("PATCH", detailKey, { pathParams: { id }, body }),
    remove: (id) => request("DELETE", detailKey, { pathParams: { id } }),
  });

  const sessions = resource("sessions", "sessionDetail");
  sessions.activate = (id, body = {}) =>
    request("POST", "sessionActivate", { pathParams: { id }, body });
  sessions.verifyUploadKey = (id, body) =>
    request("POST", "sessionVerifyUploadKey", { pathParams: { id }, body });
  sessions.deactivate = (id) =>
    request("POST", "sessionDeactivate", { pathParams: { id }, body: {} });
  sessions.archive = (id) =>
    request("POST", "sessionArchive", { pathParams: { id }, body: {} });

  const content = resource("content", "contentDetail");
  content.submit = (id) =>
    request("POST", "contentSubmit", { pathParams: { id }, body: {} });
  content.publish = (id) =>
    request("POST", "contentPublish", { pathParams: { id }, body: {} });
  content.reject = (id, rejectionReason) =>
    request("POST", "contentReject", {
      pathParams: { id },
      body: { rejectionReason: rejectionReason || "" },
    });
  content.uploadFiles = (id, files, titles = []) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    titles.forEach((title) => formData.append("titles", title));
    return request("POST", "contentFiles", {
      pathParams: { id },
      body: formData,
    });
  };
  content.deleteFile = (contentId, fileId) =>
    request("DELETE", "contentFileDetail", {
      pathParams: { id: contentId, fileId },
    });

  const liveSessions = resource("liveSessions", "liveSessionDetail");
  liveSessions.start = (id) =>
    request("POST", "liveSessionStart", { pathParams: { id }, body: {} });
  liveSessions.end = (id) =>
    request("POST", "liveSessionEnd", { pathParams: { id }, body: {} });
  liveSessions.joinUrl = (id, role = "participant", studentID) =>
    request("GET", "liveSessionJoinUrl", {
      pathParams: { id },
      query: { role, studentID },
    });
  liveSessions.recording = (id, studentID) =>
    request("GET", "liveSessionRecording", {
      pathParams: { id },
      query: { studentID },
    });
  liveSessions.bulkAttendance = (id, body) =>
    request("POST", "liveSessionBulkAttendance", {
      pathParams: { id },
      body,
    });

  const zoomIdentities = resource("zoomIdentities", "zoomIdentityDetail");
  zoomIdentities.provision = (body) =>
    request("POST", "zoomIdentityProvision", { body });
  zoomIdentities.disable = (id) =>
    request("POST", "zoomIdentityDisable", { pathParams: { id }, body: {} });

  zoomIdentities.deprovision = (id, mode = "deactivate", confirm = false) => {
    if (!["deactivate", "delete"].includes(mode)) {
      throw new Error("Invalid Zoom deprovisioning mode");
    }
    if (mode === "delete" && confirm !== true) {
      throw new Error("Permanent deletion requires explicit confirmation");
    }
    return request("POST", "zoomIdentityDeprovision", {
      pathParams: { id },
      body: { mode, ...(mode === "delete" ? { confirm: true } : {}) },
    });
  };

  const quizzes = resource("quizzes", "quizDetail");
  quizzes.attempt = (id, body) =>
    request("POST", "quizAttempt", { pathParams: { id }, body });
  quizzes.resume = (id, query) =>
    request("GET", "quizAttempt", { pathParams: { id }, query });
  quizzes.attempts = (id, query) =>
    request("GET", "quizAttemptsForQuiz", { pathParams: { id }, query });

  const quizQuestions = resource("quizQuestions", "quizQuestionDetail");
  const quizAttempts = resource("quizAttempts", "quizAttemptDetail");
  quizAttempts.submit = (id) =>
    request("POST", "quizAttemptSubmit", { pathParams: { id }, body: {} });
  quizAttempts.feedback = (id, teacherFeedback) =>
    request("PATCH", "quizAttemptFeedback", {
      pathParams: { id },
      body: { teacherFeedback },
    });
  quizAttempts.mark = (id, body) =>
    request("PATCH", "quizAttemptMark", {
      pathParams: { id },
      body,
    });

  const weeklyReports = resource("weeklyReports", "weeklyReportDetail");
  weeklyReports.review = (id) =>
    request("POST", "weeklyReportReview", { pathParams: { id }, body: {} });
  weeklyReports.approve = (id) =>
    request("POST", "weeklyReportApprove", { pathParams: { id }, body: {} });

  return {
    request,
    dashboard: () => request("GET", "dashboardHeadteacher"),
    sessions,
    content,
    contentUploadLog: (query) => request("GET", "contentUploadLog", { query }),
    liveSessions,
    liveSessionAttendance: (query) =>
      request("GET", "liveSessionAttendance", { query }),
    zoomIdentities,
    learnerActivity: (query) => request("GET", "learnerActivity", { query }),
    recordLearnerActivity: (body) =>
      request("POST", "learnerActivityRecord", { body }),
    learnerLiveSessions: (query) =>
      request("GET", "learnerLiveSessions", { query }),
    learnerContent: (query) => request("GET", "learnerContent", { query }),
    recordLearnerActivitySelf: (body) =>
      request("POST", "learnerActivitySelf", { body }),
    quizzes,
    quizQuestions,
    quizAttempts,
    weeklyReports,
  };
}
